"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { motion } from "framer-motion";
import { ShieldCheck, Truck, CreditCard, Headset, Star, Mail, BadgeCheck } from "lucide-react";
import { useCart } from "@/lib/store";
import { merveillesPresidentielles } from "@/lib/data";
import { toast } from "@/hooks/use-toast";

const stats = [
  { value: "500K+", label: "Clients Satisfaits" },
  { value: "490+", label: "Points de Vente" },
  { value: "24/7", label: "Concierge Premium" },
  { value: "99.9%", label: "Uptime Réseau" },
];

const services = [
  {
    icon: ShieldCheck,
    title: "Garantie Platinium 3 Ans",
    desc: "Extension de garantie officielle sur tous les produits premium. SAV certifié avec remplacement immédiat et technicien à domicile sous 24h.",
  },
  {
    icon: Truck,
    title: "Livraison Concierge Blanche",
    desc: "Livraison premium avec installation, configuration et démonstration à domicile. Emballage cadeau luxury et suivi GPS en temps réel.",
  },
  {
    icon: CreditCard,
    title: "Paiement Flex 6x sans Frais",
    desc: "Orange Money, Wave, Visa, Mastercard ou espèces. Payez en 3x, 6x ou 12x sans frais. Offre de crédit 0% pour les clients Platinum.",
  },
  {
    icon: Headset,
    title: "Service Concierge 24/7",
    desc: "Un dédié personnel pour chaque client Platinum. Assistance WhatsApp, chat et téléphone 24h/24. Retour sous 30 jours sans condition.",
  },
];

const payments = ["Orange Money", "Wave", "Visa Platinum", "Mastercard Gold", "Espèces", "WARI"];

export function Excellence() {
  const addItem = useCart((s) => s.addItem);
  const flagship = merveillesPresidentielles[0];

  const contacterConcierge = () => {
    toast({
      title: "📞 Concierge Premium",
      description: "Appelez le +221 33 800 00 00 ou WhatsApp +221 77 555 00 00 — disponible 24h/24 et 7j/7.",
    });
  };

  return (
    <section id="excellence" className="py-16 sm:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
          {stats.map((s, idx) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.08 }}
              className="text-center p-6 rounded-3xl bg-gradient-to-b from-[#F5FBF7] to-white border border-[#006B3F]/10"
            >
              <p className="text-3xl sm:text-4xl font-black bg-gradient-to-r from-[#006B3F] to-[#008751] bg-clip-text text-transparent">
                {s.value}
              </p>
              <p className="text-gray-500 text-sm mt-1.5">{s.label}</p>
            </motion.div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-10 items-start">
          {/* Texte */}
          <div>
            <p className="text-xs font-bold tracking-[0.3em] text-[#006B3F] uppercase mb-3">
              L&apos;Excellence Sénégalaise depuis 1985
            </p>
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 tracking-tight">
              Rejoignez l&apos;{" "}
              <span className="bg-gradient-to-r from-[#006B3F] to-[#008751] bg-clip-text text-transparent">
                Excellence
              </span>
            </h2>
            <p className="mt-4 text-gray-500 leading-relaxed">
              Plus de 500 000 Sénégalais nous font confiance. Découvrez pourquoi Senegal Telecom
              Store est la référence incontournable de la technologie de luxe en Afrique de
              l&apos;Ouest — et découvrez notre nouvelle collection présidentielle.
            </p>

            <div className="mt-8 space-y-5">
              {services.map((s) => (
                <div key={s.title} className="flex gap-4">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#006B3F] to-[#008751] flex items-center justify-center shrink-0 shadow-lg shadow-[#006B3F]/20">
                    <s.icon className="w-5 h-5 text-[#FFD700]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">{s.title}</h3>
                    <p className="text-sm text-gray-500 mt-1 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                size="lg"
                onClick={() =>
                  addItem({
                    id: flagship.id,
                    name: flagship.name,
                    price: flagship.price,
                    image: flagship.image,
                    presidential: true,
                  })
                }
                className="bg-gradient-to-r from-[#FFD700] to-[#DAA520] text-black font-bold h-12 px-7 hover:shadow-[0_0_30px_rgba(255,215,0,0.35)]"
              >
                <Star className="w-4 h-4 fill-black" /> Offre Présidentielle
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={contacterConcierge}
                className="border-[#006B3F]/40 text-[#006B3F] hover:bg-[#006B3F] hover:text-white h-12 px-7"
              >
                Contacter un concierge
              </Button>
            </div>
          </div>

          {/* Newsletter / carte Platinum */}
          <div className="space-y-5">
            <motion.div
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative overflow-hidden rounded-3xl bg-[#04140D] p-8"
            >
              <div
                className="absolute inset-0 opacity-50"
                style={{
                  background:
                    "radial-gradient(ellipse 60% 50% at 80% 10%, rgba(255,215,0,0.15), transparent), radial-gradient(ellipse 50% 50% at 10% 90%, rgba(0,107,63,0.4), transparent)",
                }}
              />
              <div className="relative">
                <div className="flex items-center gap-2 mb-4">
                  <Star className="w-4 h-4 fill-[#FFD700] text-[#FFD700]" />
                  <p className="text-[#FFD700] text-xs font-black tracking-[0.25em] uppercase">
                    Programme Platinum
                  </p>
                </div>
                <h3 className="text-2xl font-black text-white">Rejoignez l&apos;Excellence</h3>
                <p className="text-white/55 text-sm mt-2 leading-relaxed">
                  Offres exclusives, avant-premières et privilèges VIP directement dans votre
                  boîte mail.
                </p>
                <form
                  className="mt-5 flex flex-col sm:flex-row gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    toast({
                      title: "Bienvenue dans l'Excellence",
                      description: "Votre compte Platinum sera activé sous 24h. Un concierge vous contactera.",
                    });
                  }}
                >
                  <Input
                    type="email"
                    required
                    placeholder="Votre email premium"
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus-visible:ring-[#FFD700]"
                  />
                  <Button
                    type="submit"
                    className="bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black font-bold shrink-0"
                  >
                    <Mail className="w-4 h-4 mr-1" /> S&apos;inscrire
                  </Button>
                </form>
              </div>
            </motion.div>

            {/* Moyens de paiement */}
            <div className="rounded-3xl border border-gray-100 bg-gradient-to-b from-white to-[#F5FBF7] p-6 shadow-sm">
              <p className="text-xs font-bold tracking-wider text-gray-400 uppercase mb-4">
                Paiements acceptés · 6x sans frais
              </p>
              <div className="flex flex-wrap gap-2">
                {payments.map((p) => (
                  <span
                    key={p}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white border border-gray-200 text-xs font-bold text-gray-700 shadow-sm"
                  >
                    <BadgeCheck className="w-3.5 h-3.5 text-[#006B3F]" /> {p}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
